/** Serverzustand der Mailverwaltung -- F3-01, F3-04, F3-06. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/apiError';
import {
  assignInbound,
  countInbound,
  createAccount,
  createAnnouncement,
  assignMailEvent,
  createEmailTemplate,
  createFilter,
  createSignature,
  deleteAccount,
  deleteAnnouncement,
  deleteEmailTemplate,
  deleteFilter,
  deleteSignature,
  getInbound,
  listAccounts,
  listAnnouncements,
  listEmailTemplates,
  listMailEvents,
  previewEmailTemplate,
  listFilters,
  listInbound,
  listOutbound,
  listSignatures,
  pollAccounts,
  rejectInbound,
  sendOutbound,
  updateAccount,
  updateAnnouncement,
  updateEmailTemplate,
} from './api';
import type { InboundStatus } from './api';

export const mailKeys = {
  accounts: ['mail', 'accounts'] as const,
  filters: ['mail', 'filters'] as const,
  signatures: ['mail', 'signatures'] as const,
  announcements: ['mail', 'announcements'] as const,
  templates: ['mail', 'templates'] as const,
  events: ['mail', 'templates', 'events'] as const,
  outbound: ['mail', 'outbound'] as const,
  inbound: (status: InboundStatus) => ['mail', 'inbound', status] as const,
  inboundAll: ['mail', 'inbound'] as const,
  inboundCount: ['mail', 'inbound', 'count'] as const,
  inboundDetail: (id: string) => ['mail', 'inbound', 'detail', id] as const,
};

const retryUnlessPermanent = (failureCount: number, error: unknown) =>
  !(error instanceof ApiError && error.isPermanent) && failureCount < 2;

function useInvalidate(key: readonly string[]) {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: key });
  };
}

export function useMailAccounts() {
  return useQuery({
    queryKey: mailKeys.accounts,
    queryFn: listAccounts,
    retry: retryUnlessPermanent,
  });
}

export function useCreateMailAccount() {
  const invalidate = useInvalidate(mailKeys.accounts);
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => createAccount(payload),
    onSuccess: invalidate,
  });
}

export function useUpdateMailAccount() {
  const invalidate = useInvalidate(mailKeys.accounts);
  return useMutation({
    mutationFn: (variables: { accountId: string; payload: Record<string, unknown> }) =>
      updateAccount(variables.accountId, variables.payload),
    onSuccess: invalidate,
  });
}

export function useDeleteMailAccount() {
  const invalidate = useInvalidate(mailKeys.accounts);
  return useMutation({ mutationFn: deleteAccount, onSuccess: invalidate });
}

export function usePollMailAccounts() {
  const invalidate = useInvalidate(mailKeys.accounts);
  return useMutation({ mutationFn: pollAccounts, onSuccess: invalidate });
}

export function useMailFilters() {
  return useQuery({
    queryKey: mailKeys.filters,
    queryFn: listFilters,
    retry: retryUnlessPermanent,
  });
}

export function useCreateMailFilter() {
  const invalidate = useInvalidate(mailKeys.filters);
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => createFilter(payload),
    onSuccess: invalidate,
  });
}

export function useDeleteMailFilter() {
  const invalidate = useInvalidate(mailKeys.filters);
  return useMutation({ mutationFn: deleteFilter, onSuccess: invalidate });
}

export function useSignatures() {
  return useQuery({
    queryKey: mailKeys.signatures,
    queryFn: listSignatures,
    retry: retryUnlessPermanent,
  });
}

export function useCreateSignature() {
  const invalidate = useInvalidate(mailKeys.signatures);
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => createSignature(payload),
    onSuccess: invalidate,
  });
}

export function useDeleteSignature() {
  const invalidate = useInvalidate(mailKeys.signatures);
  return useMutation({ mutationFn: deleteSignature, onSuccess: invalidate });
}

export function useAnnouncements() {
  return useQuery({
    queryKey: mailKeys.announcements,
    queryFn: listAnnouncements,
    retry: retryUnlessPermanent,
  });
}

function useAnnouncementInvalidate() {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: mailKeys.announcements });
    // Das Banner haengt an einer eigenen Abfrage -- ohne diese Zeile stuende
    // eine soeben abgesetzte Stoerungsmeldung bis zu einer Minute lang nicht
    // da, und genau diese Minute ist die, in der sie gebraucht wird.
    await queryClient.invalidateQueries({ queryKey: ['announcements'] });
  };
}

export function useCreateAnnouncement() {
  const invalidate = useAnnouncementInvalidate();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => createAnnouncement(payload),
    onSuccess: invalidate,
  });
}

export function useUpdateAnnouncement() {
  const invalidate = useAnnouncementInvalidate();
  return useMutation({
    mutationFn: (variables: { announcementId: string; payload: Record<string, unknown> }) =>
      updateAnnouncement(variables.announcementId, variables.payload),
    onSuccess: invalidate,
  });
}

export function useDeleteAnnouncement() {
  const invalidate = useAnnouncementInvalidate();
  return useMutation({ mutationFn: deleteAnnouncement, onSuccess: invalidate });
}

// ---------------------------------------------------------------------------
// Mailvorlagen -- F3-05
// ---------------------------------------------------------------------------

export function useEmailTemplates() {
  return useQuery({
    queryKey: mailKeys.templates,
    queryFn: listEmailTemplates,
    retry: retryUnlessPermanent,
  });
}

export function useCreateEmailTemplate() {
  const invalidate = useInvalidate(mailKeys.templates);
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => createEmailTemplate(payload),
    onSuccess: invalidate,
  });
}

export function useUpdateEmailTemplate() {
  const invalidate = useInvalidate(mailKeys.templates);
  return useMutation({
    mutationFn: ({
      templateId,
      payload,
    }: {
      templateId: string;
      payload: Record<string, unknown>;
    }) => updateEmailTemplate(templateId, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteEmailTemplate() {
  const invalidate = useInvalidate(mailKeys.templates);
  return useMutation({
    mutationFn: (templateId: string) => deleteEmailTemplate(templateId),
    onSuccess: invalidate,
  });
}

// ---------------------------------------------------------------------------
// Versandwarteschlange -- F3-07
// ---------------------------------------------------------------------------

export function useOutbound() {
  return useQuery({
    queryKey: mailKeys.outbound,
    queryFn: listOutbound,
    // Eine halbe Minute: Wer die Warteschlange offen hat, wartet darauf, dass
    // sich etwas bewegt.
    staleTime: 30 * 1000,
    retry: retryUnlessPermanent,
  });
}

export function useSendOutbound() {
  const invalidate = useInvalidate(mailKeys.outbound);
  return useMutation({
    mutationFn: sendOutbound,
    onSuccess: invalidate,
  });
}

// ---------------------------------------------------------------------------
// Anfrage-Queue -- F5-03
// ---------------------------------------------------------------------------

export function useInboundQueue(status: InboundStatus) {
  return useQuery({
    queryKey: mailKeys.inbound(status),
    queryFn: () => listInbound(status),
    // Eine halbe Minute, wie bei der Versandwarteschlange: Wer die Queue
    // offen hat, arbeitet sie gerade ab.
    staleTime: 30 * 1000,
    retry: retryUnlessPermanent,
  });
}

/**
 * Eine Anfrage mit vollstaendigem Text -- erst geladen, wenn jemand sie
 * oeffnet. Der Schluessel liegt unter ``inboundAll``, damit Zuweisung und
 * Ablehnung auch den geoeffneten Stand erneuern.
 */
export function useInboundMessage(inboundId: string) {
  return useQuery({
    queryKey: mailKeys.inboundDetail(inboundId),
    queryFn: () => getInbound(inboundId),
    staleTime: 5 * 60 * 1000,
    retry: retryUnlessPermanent,
  });
}

/**
 * Die Zahl der offenen Anfragen -- fuer die Blase in der Ticketliste.
 *
 * ``enabled`` und nicht "einfach fragen": Ohne ``mail.triage`` antwortet der
 * Server mit 403, und die Ticketliste zeigte bei jedem Aufruf einen Fehler
 * fuer etwas, das der Nutzer gar nicht sehen soll.
 *
 * Eine Minute Ruhe: Die Zahl aendert sich, wenn der Abruf laeuft -- also
 * hoechstens jede Minute. Haeufiger zu fragen erzeugt Last fuer eine Zahl,
 * die sich nicht bewegt.
 */
export function useInboundCount(enabled: boolean) {
  return useQuery({
    queryKey: mailKeys.inboundCount,
    queryFn: () => countInbound('open'),
    enabled,
    staleTime: 60 * 1000,
    retry: retryUnlessPermanent,
  });
}

/**
 * Nach Zuweisung und Ablehnung werden **alle drei** Listen ungueltig.
 *
 * Die Zeile wandert von "offen" nach "zugewiesen" oder "abgelehnt" -- wer nur
 * die gerade sichtbare Liste erneuert, findet sie im Archiv beim naechsten
 * Blick nicht wieder und glaubt, die Entscheidung sei verloren gegangen.
 */
function useInboundInvalidate() {
  const queryClient = useQueryClient();
  return async () => {
    // ``inboundAll`` deckt die Zaehlung mit ab: Ihr Schluessel beginnt mit
    // denselben beiden Gliedern. Die Blase soll unmittelbar kleiner werden --
    // eine Zahl, die nach der Entscheidung stehen bleibt, sieht aus, als
    // waere sie nicht angekommen.
    await queryClient.invalidateQueries({ queryKey: mailKeys.inboundAll });
    // Das Ticket, das gerade entstanden ist, gehoert in die Ticketliste.
    await queryClient.invalidateQueries({ queryKey: ['tickets'] });
  };
}

export function useAssignInbound() {
  const invalidate = useInboundInvalidate();
  return useMutation({
    mutationFn: (variables: {
      inboundId: string;
      payload: { customer_id: string; contact_id?: string | null; create_contact?: boolean };
    }) => assignInbound(variables.inboundId, variables.payload),
    onSuccess: invalidate,
  });
}

export function useRejectInbound() {
  const invalidate = useInboundInvalidate();
  return useMutation({
    mutationFn: (variables: { inboundId: string; payload: { reason?: string | null } }) =>
      rejectInbound(variables.inboundId, variables.payload),
    onSuccess: invalidate,
  });
}

/** Die Anlaesse mit ihrer Vorlage -- F3-21. Unter dem Schluessel der
 *  Vorlagen, damit jede Aenderung an einer Vorlage auch die Anlaesse neu
 *  laedt: Beide Listen zeigen die Zuweisung. */
export function useMailEvents() {
  return useQuery({
    queryKey: mailKeys.events,
    queryFn: listMailEvents,
  });
}

export function useAssignMailEvent() {
  const invalidate = useInvalidate(mailKeys.templates);
  return useMutation({
    mutationFn: ({ eventKey, templateId }: { eventKey: string; templateId: string | null }) =>
      assignMailEvent(eventKey, templateId),
    onSuccess: invalidate,
  });
}

export function usePreviewEmailTemplate() {
  return useMutation({ mutationFn: previewEmailTemplate });
}
